import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
} from "../ui";
import LudDashboardModal from "./LudDashboardModal";
import { useSubmitGuard } from "./modalHooks";
import { describeHoursProblem } from "../../utils/workingHoursErrors";
import "./WorkingHoursModal.css";

export default function WorkingHoursModal({
  open,
  lang,
  text,
  branch,
  hours,
  requestMode = false,
  onClose,
  onSubmit,
}) {
  // Repeat clicks are ignored while a save returned by the caller is running.
  const [saving, runSave] = useSubmitGuard();
  const [form, setForm] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowErrors(false);
      return;
    }

    setForm(
      (hours || []).map((day) => ({
        ...day,
      })),
    );
    // Initialise when the modal opens or the entity changes, not whenever the
    // parent passes a new object/array for the same data (that wiped edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branch?.id]);

  const updateDay = (index, key, value) => {
    setForm((current) =>
      current.map((day, dayIndex) =>
        dayIndex === index
          ? {
              ...day,
              [key]: value,
            }
          : day,
      ),
    );
  };

  const toggleSecondShift = (index, enabled) => {
    setForm((current) =>
      current.map((day, dayIndex) =>
        dayIndex === index
          ? { ...day, from2: enabled ? "17:00" : "", to2: enabled ? "23:00" : "" }
          : day,
      ),
    );
  };

  // Copies one day's schedule onto every day (each day keeps its own name).
  const copyToAll = (index) => {
    setForm((current) => {
      const source = current[index];
      return current.map((day) => ({
        ...day,
        closed: source.closed,
        from1: source.from1,
        to1: source.to1,
        from2: source.from2,
        to2: source.to2,
      }));
    });
  };

  // Each open day needs a complete first shift; a second shift is optional but
  // must be complete. Overnight ranges (e.g. 17:00 -> 01:00) are valid.
  const errors = form.map((day) => {
    if (day.closed) return null;
    if (!day.from1 || !day.to1) return "first";
    if (Boolean(day.from2) !== Boolean(day.to2)) return "second";
    return null;
  });
  const hasErrors = errors.some(Boolean);

  // What the request would change, compared with the hours the branch has now
  // (shown before sending; nothing changes until the request is processed).
  const shiftsOf = (day) =>
    day.closed
      ? []
      : [
          [day.from1, day.to1],
          [day.from2, day.to2],
        ]
          .filter(([from, to]) => from && to)
          .map(([from, to]) => ({ text: `${from} - ${to}`, overnight: to < from }));
  const describeDay = (day) => {
    const shifts = shiftsOf(day);
    if (!shifts.length) return lang === "ar" ? "مغلق" : "Closed";
    return shifts
      .map((shift) => (shift.overnight ? `${shift.text} (${lang === "ar" ? "تنتهي في اليوم التالي" : "ends next day"})` : shift.text))
      .join(" / ");
  };
  const changes = requestMode
    ? form
        .map((day, index) => ({ day, before: (hours || [])[index] }))
        .filter(({ day, before }) => before && describeDay(day) !== describeDay(before))
    : [];

  const submit = () => {
    if (hasErrors) {
      setShowErrors(true);
      return undefined;
    }
    if (requestMode && !changes.length) return undefined;
    return runSave(onSubmit, form);
  };

  return (
    <LudDashboardModal
      open={open}
      busy={saving}
      title={`${lang === "ar"
        ? requestMode
          ? "طلب تعديل أوقات العمل"
          : "تعديل أوقات العمل"
        : requestMode
          ? "Request Working Hours Change"
          : "Edit Working Hours"}${
        branch
          ? ` - ${
              lang === "ar"
                ? branch.nameAr
                : branch.nameEn
            }`
          : ""
      }`}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="primary"
            appearance="filled"
            onClick={submit}
            disabled={saving || (requestMode && !hasErrors && !changes.length)}
            loading={saving}
          >
            {requestMode
              ? lang === "ar"
                ? "إرسال الطلب"
                : "Send Request"
              : text.save}
          </Button>

          <Button
            variant="danger"
            appearance="soft"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
        </>
      }
    >
      <div className="lud-hours-editor">
        {requestMode && (
          <section className="lud-hours-summary" aria-label={lang === "ar" ? "ملخص التغييرات المطلوبة" : "Summary of requested changes"}>
            <h4>{lang === "ar" ? "ملخص التغييرات المطلوبة" : "Summary of requested changes"}</h4>
            {changes.length ? (
              <ul>
                {changes.map(({ day, before }) => (
                  <li key={day.dayEn}>
                    <strong>{lang === "ar" ? day.dayAr : day.dayEn}</strong>
                    <span dir="ltr">{describeDay(before)}</span>
                    <span aria-hidden="true">{lang === "ar" ? "←" : "→"}</span>
                    <span dir="ltr">{describeDay(day)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="lud-hours-hint">{lang === "ar" ? "لم تُغيّر أي يوم بعد." : "No day has been changed yet."}</p>
            )}
            <p className="lud-hours-hint">
              {lang === "ar"
                ? "لن تتغير أوقات الفرع الآن: يراجع المطعم أو إدارة LUD الطلب ثم تؤكد التطبيقات التعديل."
                : "The branch hours do not change now: the restaurant or LUD reviews the request, then the applications confirm the change."}
            </p>
          </section>
        )}
        <p className="lud-hours-hint">
          {lang === "ar"
            ? "حدد أيام العمل وأوقات كل فترة. يمكن أن تنتهي الفترة بعد منتصف الليل (مثلاً من 17:00 إلى 01:00)."
            : "Set the open days and the times of each shift. A shift may end after midnight (e.g. 17:00 to 01:00)."}
        </p>
        {form.map((day, index) => {
          const hasSecond = Boolean(day.from2 || day.to2);
          const problem = showErrors ? errors[index] : null;
          return (
            <div
              className={`lud-hours-day${day.closed ? " is-closed" : ""}${problem ? " has-error" : ""}`}
              key={day.dayEn}
            >
              <div className="lud-hours-day-head">
                <div className="lud-hours-day-name">
                  {lang === "ar" ? day.dayAr : day.dayEn}
                </div>
                <Checkbox
                  checked={!day.closed}
                  onChange={(event) => updateDay(index, "closed", !event.target.checked)}
                  label={lang === "ar" ? "يوم عمل" : "Open"}
                />
                <Button
                  size="sm"
                  appearance="ghost"
                  onClick={() => copyToAll(index)}
                  title={lang === "ar" ? "نسخ هذا اليوم إلى كل الأيام" : "Copy this day to all days"}
                >
                  {lang === "ar" ? "تطبيق على الكل" : "Apply to all"}
                </Button>
              </div>

              {!day.closed && (
                <div className="lud-hours-day-shifts">
                  <div className="lud-hours-shift">
                    <Input
                      type="time"
                      label={text.firstShift}
                      value={day.from1 || ""}
                      error={problem === "first" && !day.from1}
                      onChange={(event) => updateDay(index, "from1", event.target.value)}
                    />
                    <Input
                      type="time"
                      label={lang === "ar" ? "إلى" : "To"}
                      value={day.to1 || ""}
                      error={problem === "first" && !day.to1}
                      onChange={(event) => updateDay(index, "to1", event.target.value)}
                    />
                  </div>
                  {hasSecond ? (
                    <div className="lud-hours-shift">
                      <Input
                        type="time"
                        label={text.secondShift}
                        value={day.from2 || ""}
                        error={problem === "second" && !day.from2}
                        onChange={(event) => updateDay(index, "from2", event.target.value)}
                      />
                      <Input
                        type="time"
                        label={lang === "ar" ? "إلى" : "To"}
                        value={day.to2 || ""}
                        error={problem === "second" && !day.to2}
                        onChange={(event) => updateDay(index, "to2", event.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="danger"
                        appearance="ghost"
                        onClick={() => toggleSecondShift(index, false)}
                      >
                        {lang === "ar" ? "إزالة الفترة الثانية" : "Remove 2nd shift"}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      appearance="soft"
                      className="lud-hours-add-shift"
                      onClick={() => toggleSecondShift(index, true)}
                    >
                      {lang === "ar" ? "+ فترة ثانية" : "+ Second shift"}
                    </Button>
                  )}
                </div>
              )}
              {problem && (
                <p className="lud-hours-error" role="alert">
                  {describeHoursProblem(
                    {
                      issue: "incomplete",
                      shift: problem === "first" ? "morning" : "evening",
                      dayLabel: lang === "ar" ? day.dayAr : day.dayEn,
                    },
                    lang,
                  )}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </LudDashboardModal>
  );
}
